import { useState, useCallback, useEffect, useMemo } from 'react';
import { searchNeighbors } from '../api';
import { FACES_PER_PAGE, HIGH_CONFIDENCE_THRESHOLD } from '../utils/constants';

/**
 * Custom hook for managing neighbor (similar face) search state and operations.
 * Handles neighbor loading, filtering, and pagination.
 *
 * @param {Object} options - Hook options
 * @param {Object} options.selectedFace - Current selected face with embedding
 * @param {string} options.selectedImage - Current selected image ID (for filtering)
 * @returns {Object} Neighbor search state and handlers
 */
export default function useNeighborSearch({ selectedFace, selectedImage }) {
  // Neighbor state
  const [neighbors, setNeighbors] = useState([]);
  const [loadingNeighbors, setLoadingNeighbors] = useState(false);
  const [neighborsPage, setNeighborsPage] = useState(1);
  const [threshold, setThreshold] = useState(0.35);

  /**
   * Load neighbors for a face embedding
   */
  const loadNeighbors = useCallback(async (face) => {
    if (!face || !face.embedding) {
      setNeighbors([]);
      return;
    }
    setLoadingNeighbors(true);
    try {
      const res = await searchNeighbors(face.embedding, 5);
      setNeighbors(res);
    } catch (e) {
      console.error(e);
      setNeighbors([]);
    } finally {
      setLoadingNeighbors(false);
    }
  }, []);

  // Load neighbors when selectedFace changes
  useEffect(() => {
    loadNeighbors(selectedFace);
  }, [selectedFace, loadNeighbors]);

  // Reset page when neighbors change
  useEffect(() => {
    setNeighborsPage(1);
  }, [neighbors]);

  // Filter neighbors to only show high confidence matches
  // Also exclude faces from the same image as the selected face
  const currentImageId = selectedFace?.image_id || selectedImage;
  const highConfNeighbors = useMemo(() => {
    return (neighbors || []).filter((n) => {
      if (n.distance == null) return false;
      // Exclude faces from the same image
      if (n.image_id && currentImageId && n.image_id === currentImageId) return false;
      const score = Math.max(0, 1 - n.distance);
      return score >= HIGH_CONFIDENCE_THRESHOLD;
    });
  }, [neighbors, currentImageId]);

  // Pagination calculations
  const totalNeighborPages = useMemo(() => {
    return Math.max(1, Math.ceil((highConfNeighbors.length || 0) / FACES_PER_PAGE));
  }, [highConfNeighbors.length]);

  const currentNeighborPage = useMemo(() => {
    return Math.min(neighborsPage, totalNeighborPages);
  }, [neighborsPage, totalNeighborPages]);

  const pageNeighbors = useMemo(() => {
    return highConfNeighbors.slice(
      (currentNeighborPage - 1) * FACES_PER_PAGE,
      currentNeighborPage * FACES_PER_PAGE
    );
  }, [highConfNeighbors, currentNeighborPage]);

  /**
   * Go to previous page of neighbors
   */
  const prevNeighborsPage = useCallback(() => {
    setNeighborsPage((p) => Math.max(1, p - 1));
  }, []);

  /**
   * Go to next page of neighbors
   */
  const nextNeighborsPage = useCallback(() => {
    setNeighborsPage((p) => Math.min(totalNeighborPages, p + 1));
  }, [totalNeighborPages]);

  return {
    // Raw state
    neighbors,
    loadingNeighbors,
    neighborsPage,
    setNeighborsPage,
    threshold,
    setThreshold,

    // Derived state
    highConfNeighbors,
    totalNeighborPages,
    currentNeighborPage,
    pageNeighbors,

    // Handlers
    loadNeighbors,
    prevNeighborsPage,
    nextNeighborsPage,
  };
}
